import type { Leak } from "../types/leak";
import { cleanTitle, getDisplayForum } from "./cleaners";

// Country name / code mapping to flag emojis
const COUNTRY_FLAG_MAP: Record<string, string> = {
  us: "🇺🇸",
  usa: "🇺🇸",
  "united states": "🇺🇸",
  "united states of america": "🇺🇸",
  in: "🇮🇳",
  ind: "🇮🇳",
  india: "🇮🇳",
  gb: "🇬🇧",
  uk: "🇬🇧",
  "united kingdom": "🇬🇧",
  ca: "🇨🇦",
  can: "🇨🇦",
  canada: "🇨🇦",
  au: "🇦🇺",
  aus: "🇦🇺",
  australia: "🇦🇺",
  de: "🇩🇪",
  deu: "🇩🇪",
  germany: "🇩🇪",
  fr: "🇫🇷",
  fra: "🇫🇷",
  france: "🇫🇷",
  it: "🇮🇹",
  ita: "🇮🇹",
  italy: "🇮🇹",
  es: "🇪🇸",
  esp: "🇪🇸",
  spain: "🇪🇸",
  br: "🇧🇷",
  bra: "🇧🇷",
  brazil: "🇧🇷",
  mx: "🇲🇽",
  mex: "🇲🇽",
  mexico: "🇲🇽",
  id: "🇮🇩",
  idn: "🇮🇩",
  indonesia: "🇮🇩",
  jp: "🇯🇵",
  japan: "🇯🇵",
  kr: "🇰🇷",
  korea: "🇰🇷",
  "south korea": "🇰🇷",
  nl: "🇳🇱",
  netherlands: "🇳🇱",
  sg: "🇸🇬",
  singapore: "🇸🇬",
  ae: "🇦🇪",
  uae: "🇦🇪",
  "united arab emirates": "🇦🇪",
  ch: "🇨🇭",
  switzerland: "🇨🇭",
  se: "🇸🇪",
  sweden: "🇸🇪",
  il: "🇮🇱",
  israel: "🇮🇱",
  tr: "🇹🇷",
  turkey: "🇹🇷",
  sa: "🇸🇦",
  "saudi arabia": "🇸🇦",
};

/**
 * Known ransomware groups / blogs in darknet feeds.
 * ONLY records originating from or classified as ransomware groups are labeled "Ransomware Alert".
 */
export const KNOWN_RANSOMWARE_GROUPS = [
  "space_bears",
  "dragonforce",
  "lockbit",
  "lockbit3",
  "lockbit_blog",
  "blackbasta",
  "play",
  "clop",
  "clop_leaks",
  "alphv",
  "qilin",
  "8base",
  "akira",
  "bianlian",
  "blackcat",
  "hunters",
  "medusa",
  "noescape",
  "rhysida",
  "snatch",
  "stormous",
  "safe_pay",
  "deadlock_blogs",
  "ailock",
  "ransome_house",
  "ransomexx",
  "trident_locker",
  "shinobi",
  "gsg",
  "genesis",
  "lamashtu_blog",
  "redact",
  "dls",
  "exfilsquad",
  "gammax",
  "securotrop",
  "shiba",
  "titan",
  "dark_project",
  "direwolf",
  "doommageddo",
  "fulcrumsec",
  "helix",
  "kairos",
  "kazu",
  "krybit",
  "lynx",
  "m3rx",
  "money_message",
  "ms13",
  "my_data",
  "net_runner",
  "niflheim",
  "nitrogen",
  "nova_blog",
  "orion",
  "orion_leaks",
  "orova",
  "orova_blog",
  "sovcali",
  "abyss",
  "anubis",
  "arcus",
  "aurora_blog",
  "bashe",
  "booba_project",
  "brain_cipher",
  "chaos",
  "coinbase_cartel",
  "daixin_team",
  "embargo",
  "everest",
  "insomnia",
  "interlock",
  "payload",
  "pear",
  "radar",
  "sarcoma_groups",
  "termite",
  "underground",
];

export function getCountryFlagEmoji(countryStr?: string | null): string {
  if (!countryStr || typeof countryStr !== "string") return "";
  const cleaned = countryStr.toLowerCase().trim();
  // Direct match
  if (COUNTRY_FLAG_MAP[cleaned]) {
    return COUNTRY_FLAG_MAP[cleaned];
  }
  // Check if country string contains known country key
  for (const [key, flag] of Object.entries(COUNTRY_FLAG_MAP)) {
    if (cleaned === key || cleaned.startsWith(`${key} `) || cleaned.endsWith(` ${key}`) || cleaned.includes(` ${key} `)) {
      return flag;
    }
  }
  // 2-letter ISO code fallback to Regional Indicator Symbol pair
  if (cleaned.length === 2 && /^[a-z]{2}$/.test(cleaned)) {
    const codePoints = [...cleaned].map((c) => 127397 + c.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  }
  return "";
}

export function getIncidentCategory(leak: Partial<Leak>): { title: string; emoji: string } {
  const forumClean = (leak.forum || "").toLowerCase().trim();
  const isRansomware =
    leak.forum_type === "ransomware" ||
    forumClean.includes("ransom") ||
    KNOWN_RANSOMWARE_GROUPS.some((g) => forumClean.includes(g));

  if (isRansomware) {
    return { title: "Ransomware Alert", emoji: "📢" };
  }

  const dataTypes = leak.data_types || [];
  if (dataTypes.includes("Database")) {
    return { title: "Database Leak Alert", emoji: "📢" };
  }
  if (dataTypes.includes("Credentials")) {
    return { title: "Credential Exposure Alert", emoji: "📢" };
  }
  if (dataTypes.includes("PII") || dataTypes.includes("Financial") || dataTypes.includes("Personal Data")) {
    return { title: "Data Leak Alert", emoji: "📢" };
  }

  return { title: "Cybersecurity Incident", emoji: "📢" };
}

export function formatReportedDate(dateVal?: string | Date | null): string | null {
  if (!dateVal) return null;
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return null;
  const day = d.getDate();
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
}

export function cleanWebsite(siteStr?: string | null): string | null {
  if (!siteStr || typeof siteStr !== "string") return null;
  const trimmed = siteStr.trim();
  if (!trimmed || trimmed === "-" || trimmed.toLowerCase() === "n/a") return null;
  return trimmed.replace(/^https?:\/\//i, "").replace(/\/+$/, "");
}

const RANSOMWARE_DISPLAY_NAMES: Record<string, string> = {
  space_bears: "Space Bears",
  dragonforce: "DragonForce",
  lockbit: "LockBit",
  lockbit3: "LockBit 3.0",
  lockbit_blog: "LockBit",
  blackbasta: "BlackBasta",
  play: "Play",
  clop: "Clop",
  clop_leaks: "Clop",
  alphv: "ALPHV / BlackCat",
  blackcat: "BlackCat",
  qilin: "Qilin",
  "8base": "8Base",
  akira: "Akira",
  bianlian: "BianLian",
  hunters: "Hunters International",
  medusa: "Medusa",
  noescape: "NoEscape",
  rhysida: "Rhysida",
  snatch: "Snatch",
  stormous: "Stormous",
  safe_pay: "SafePay",
  deadlock_blogs: "DeadLock",
  ailock: "AiLock",
  ransome_house: "RansomHouse",
  ransomexx: "RansomEXX",
  trident_locker: "Trident Locker",
  shinobi: "Shinobi",
  gsg: "GSG",
  genesis: "Genesis",
  lamashtu_blog: "Lamashtu",
  redact: "Redact",
  daixin_team: "Daixin Team",
  exfilsquad: "ExfilSquad",
  dark_project: "Dark Project",
  direwolf: "DireWolf",
  gammax: "GammaX",
  securotrop: "Securotrop",
  orova_blog: "Orova",
  orova: "Orova",
  aurora_blog: "Aurora",
  nova_blog: "Nova",
  booba_project: "Booba",
  brain_cipher: "Brain Cipher",
  coinbase_cartel: "Coinbase Cartel",
  night_spire: "Night Spire",
  money_message: "Money Message",
  net_runner: "NetRunner",
  sarcoma_groups: "Sarcoma",
  shiba: "Shiba",
  titan: "Titan",
  dls: "DLS",
  kairos: "Kairos",
  abyss: "Abyss",
  anubis: "Anubis",
  arcus: "Arcus",
  bashe: "Bashe",
  chaos: "Chaos",
  embargo: "Embargo",
  everest: "Everest",
  insomnia: "Insomnia",
  interlock: "Interlock",
  lynx: "Lynx",
  payload: "Payload",
  pear: "Pear",
  radar: "Radar",
  termite: "Termite",
  underground: "Underground",
};

/**
 * Extract threat actor accurately according to provenance rules:
 * - If forum is a ransomware group blog, threat actor is the group display name.
 * - If forum is a public sharing forum, threat actor is author_details / threat_actor if available.
 * - Never use source forum name as the victim organization.
 */
export function getVerifiedThreatActor(leak: Partial<Leak>): string | null {
  const forumClean = (leak.forum || "").toLowerCase().trim();
  const isRansomwareGroup =
    leak.forum_type === "ransomware" ||
    KNOWN_RANSOMWARE_GROUPS.some((g) => forumClean.includes(g));

  if (isRansomwareGroup) {
    if (RANSOMWARE_DISPLAY_NAMES[forumClean]) {
      return RANSOMWARE_DISPLAY_NAMES[forumClean];
    }
    // Format words, e.g. "space_bears" -> "Space Bears"
    return forumClean
      .split(/[_\-\s]+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }

  // Non-ransomware forum (e.g. darkforum, xforums, warriors, breachforums)
  if (leak.threat_actor && leak.threat_actor !== "-" && leak.threat_actor.trim()) {
    return leak.threat_actor.trim();
  }
  if (leak.author_details && leak.author_details !== "-" && leak.author_details.trim()) {
    return leak.author_details.trim();
  }

  return null;
}

/**
 * Formats a verified X/Twitter post based strictly on real leak fields.
 * Omits lines for any missing or unverified fields.
 */
export function formatXPost(leak: Partial<Leak>): string {
  const category = getIncidentCategory(leak);
  const forumClean = (leak.forum || "").toLowerCase().trim();
  const isRansomware =
    leak.forum_type === "ransomware" ||
    forumClean.includes("ransom") ||
    KNOWN_RANSOMWARE_GROUPS.some((g) => forumClean.includes(g));

  // Determine location with fallback to stock ticker (e.g. NYSE:PH -> United States)
  let locationRaw = leak.country || (leak.countries && leak.countries[0]) || leak.location || null;
  if (!locationRaw && (leak as any).stock_ticker) {
    const ticker = String((leak as any).stock_ticker);
    if (ticker.includes("NYSE:") || ticker.includes("NASDAQ:") || ticker.includes("AMEX:")) {
      locationRaw = "United States";
    }
  }

  const flagEmoji = locationRaw ? getCountryFlagEmoji(locationRaw) : "";

  // Headline
  const headline = flagEmoji
    ? `${category.emoji} ${category.title}: ${flagEmoji}`
    : `${category.emoji} ${category.title}`;

  // Structured details
  const details: string[] = [];

  const threatActor = getVerifiedThreatActor(leak);
  if (threatActor) {
    details.push(`🛡️ Threat Actor: ${threatActor}`);
  }

  // Organization: fallback to company, victim, or for ransomware alerts, the victim target title
  const rawOrg =
    leak.company_name ||
    (leak as any).company ||
    leak.victim ||
    (isRansomware ? (leak.title || leak.thread_title || leak.thread_title_list) : null);

  if (rawOrg && typeof rawOrg === "string" && rawOrg.trim() && rawOrg !== "-" && rawOrg.toLowerCase() !== "unknown") {
    details.push(`🏢 Organization: ${rawOrg.trim()}`);
  }

  if (locationRaw && locationRaw.trim() && locationRaw !== "-") {
    details.push(`📍 Location: ${locationRaw.trim()}`);
  }

  // Sector: fallback to primary_sector, sectors[0], category, industry
  const rawSector =
    leak.primary_sector ||
    (leak.sectors && leak.sectors[0]) ||
    (leak as any).category ||
    (leak as any).industry ||
    leak.sector ||
    null;

  if (rawSector && typeof rawSector === "string" && rawSector.trim() && rawSector !== "-" && rawSector.toLowerCase() !== "unknown") {
    details.push(`🏭 Sector: ${rawSector.trim()}`);
  }

  // Website: fallback to company_website, domain, website, or clearweb link
  let site = cleanWebsite(
    leak.domain ||
    leak.site ||
    leak.website ||
    (leak as any).company_website ||
    (leak as any).company_domain
  );

  if (!site && Array.isArray((leak as any).links)) {
    for (const l of (leak as any).links) {
      if (
        typeof l === "string" &&
        !l.includes(".onion") &&
        !l.includes("gofile.io") &&
        !l.includes("mega.nz") &&
        l.startsWith("http")
      ) {
        site = cleanWebsite(l);
        if (site) break;
      }
    }
  }

  if (site) {
    details.push(`🌐 Website: ${site}`);
  }

  const reportedDate = formatReportedDate(
    leak.created_at || (leak as any).upload_datetime || (leak as any).upload_date_raw
  );
  if (reportedDate) {
    details.push(`📅 Reported on: ${reportedDate}`);
  }

  // If no details at all were extractable, include cleaned title safely
  if (details.length === 0) {
    const fallbackTitle = cleanTitle(leak.title || leak.thread_title || leak.thread_title_list || "Incident reported");
    return `${headline}\n\n🔎 Key Details:\n📄 Summary: ${fallbackTitle}`;
  }

  return `${headline}\n\n🔎 Key Details:\n${details.join("\n")}`;
}
